import {define, inject, singleton} from '@injex/core';
import {intermittentLoop} from '../../../common/utils/intermittentLoop';
import {formatNumber, isObject, keyBy} from '../../../common/utils/utils';
import {guid} from '@vidazoo/ui-kit';
import IReportingResults from '../common/interfaces/IReportingResults';
import LinksByVerticalManager from '../../../common/managers/linksByVerticalManager.mdl';
import ReportingMetaDataManager from '../../../common/managers/ReportingMetaDataManager.mdl';
import {computed, makeObservable, observable} from 'mobx';
import {IBiReportingParams} from '../common/interfaces/IReportingParams';
import IReportingResultsRow from '../common/interfaces/IReportingResultsRow';
import {
    IReportingPreparedField,
    IReportingPreparedResults,
    IReportingPreparedResultsRow
} from '../common/interfaces/IReportingPreparedResults';
import {cloneDeep} from 'lodash';
import ReportingParamsManager from "./reportingParamsManager.mdl";

const DATA_CHUNK_SIZE: number = 50;

@define()
@singleton()
export default class ReportingBuildManager {

    @inject() private linksByVerticalManager: LinksByVerticalManager;
    @inject() private reportingMetaDataManager: ReportingMetaDataManager;
    @inject() private reportingParamsManager: ReportingParamsManager;

    @observable public requestedParams: IBiReportingParams;
    @observable public preparedRows: any[] = [];
    @observable public requestedParamsByKey: { [key: string]: number } = {};

    constructor() {
        makeObservable(this);
    }

    public setRequestedParams(requestedParams: IBiReportingParams) {
        this.requestedParams = cloneDeep(requestedParams);
        this.handleSubGroups();
    }

    public buildResults(data: IReportingResultsRow[]): Promise<IReportingPreparedResults> {

        return new Promise((resolve, reject) => {
            this.preparedRows = [];

            try {
                intermittentLoop(data, (item, index) => this.handleRow(item, index), DATA_CHUNK_SIZE, () => {
                    const results = {
                        result: this.preparedRows,
                        fields: this.preparedRows.length ? this.preparedRows[0].fields : [],
                        groups: this.preparedRows.length ? this.preparedRows[0].groups : [],
                        originResults: data
                    };

                    resolve(results);
                });
            } catch (e) {
                reject(e);
            }
        });
    }

    private handleRow(item: IReportingResultsRow, idx: number) {
        const dto: IReportingPreparedResultsRow = {
            id: guid(),
            fields: [],
            groups: [],
            indexed: {}
        };

        const requestedFields = [];
        const nonRequestedFields = [];

        this.handleFields(item, dto, requestedFields, nonRequestedFields);
        this.handleGroups(item, dto);

        dto.fields = [...requestedFields, ...nonRequestedFields];

        dto.indexed = keyBy(dto.groups.concat(dto.fields as any), "name");

        this.preparedRows.push(dto);
    }

    private handleFields(item: IReportingResultsRow, dto: IReportingResults, requestedFields: any[], nonRequestedFields: any[]) {

        for (const fieldName in item.fields) {

            const value = item.fields[fieldName];

            const fieldFromContext = this.reportingMetaDataManager.getFieldByValue(fieldName, this.requestedParams.verticalType);
            const requestedFieldIndex = this.requestedParams.fields.findIndex((field) => field.value === fieldName);
            const isRequested = requestedFieldIndex > -1;

            const fieldDto: IReportingPreparedField = {
                id: guid(),
                name: fieldName,
                key: fieldName,
                value,
                isRequested,
            };

            if (fieldFromContext) {
                fieldDto.type = fieldFromContext.type;
                fieldDto.name = fieldFromContext.label;

                if (this.requestedParams.isCompare) {
                    const [originalValue, compareValue, delta] = fieldDto.value;

                    fieldDto.displayValue = [originalValue, compareValue, delta].map((val) => {
                        return fieldFromContext.format
                            ? fieldFromContext.format(val)
                            : formatNumber(val, 2);
                    });
                } else {
                    try {
                        fieldDto.displayValue = fieldFromContext.format
                            ? fieldFromContext.format(fieldDto.value)
                            : formatNumber(fieldDto.value, 2);
                    } catch (e) {
                        console.error(e);
                    }
                }

            }

            // this is critical in order to preserve the requested fields order
            if (isRequested) {
                requestedFields[requestedFieldIndex] = fieldDto;
            } else {
                nonRequestedFields.push(fieldDto);
            }

            dto.fields.push(fieldDto);
        }
    }

    private handleGroups(item: IReportingResultsRow, dto: IReportingResults) {
        this.requestedParams.groups.forEach((group, gIndex) => {
            const value = item.groups[group.value];

            if (isObject(value)) {
                for (const key in value) {
                    const valueInObj = typeof value[key] === "boolean" ? JSON.stringify(value[key]) : value[key];
                    dto.groups.push({
                        id: gIndex + key,
                        key: group.value,
                        name: `${group.value}.${key}`,
                        valueDisplay: valueInObj || "",
                        value: valueInObj || "",
                        link: "",
                        originalId: "",
                        hasFilterType: !!group.filterType
                    });
                }
            } else {
                const originalId = group.value === "advertiserName" ? item.groups["advertiserId"] : item.groups[`${group.value}Original`];
                const valueName = item.groups[`${group.value}ValueName`];
                const userId = item.groups.userId ? item.groups.userId : null;
                let link: string;
                if (originalId) {
                    link = this.linksByVerticalManager.getLink(originalId, group, this.requestedParams.verticalType, userId);
                }
                if (valueName) {
                    link = this.linksByVerticalManager.getLink(value, group, this.requestedParams.verticalType, userId);
                }
                const groupObj = {
                    id: gIndex,
                    key: group.value,
                    name: group.label,
                    valueDisplay: value,
                    value: valueName || value,
                    link,
                    originalId,
                    hasFilterType: !!group.filterType,
                    isGroup: true
                };
                dto.groups.push(groupObj);
            }
        });
    }

    private handleSubGroups() {
        this.requestedParams.subGroups.forEach((subGroup, gIndex) => {
            subGroup.entities.forEach((entity) => {
                if (entity === "targeting") {
                    subGroup.targeting.types.forEach((targetingType) => {
                        subGroup.targeting.operators.forEach((targetingOperator) => {
                            const entityTargeting = `targeting:${targetingType}:${targetingOperator}`;
                            this.requestedParams.groups.push({label: entityTargeting, value: entityTargeting} as any);
                        });
                    });
                } else if (entity === "optionsWidget" && subGroup.freeField) {
                    this.requestedParams.groups.push({
                        label: `optionsWidget:${subGroup.freeField}`,
                        value: `optionsWidget:${subGroup.freeField}`
                    } as any);

                } else {
                    this.requestedParams.groups.push({label: entity, value: entity} as any);
                }
            });
        });
    }

    @computed
    public get requestedGroups() {
        // const groupsParams = this.reportingParamsManager.params.groups.map(paramG => paramG.value);
        // this.reportingParamsManager.params.subGroups.forEach(subGroup => {
        //     groupsParams.push(...subGroup.entities);
        // }
        // return this.requestedParams.groups ? this.requestedParams.groups.filter(g => groupsParams.includes(g.value)) : []
        return this.requestedParams.groups
    }

    @computed
    public get requestedFields() {
        const selected = this.reportingParamsManager.params.fields;
        return selected.filter(field => field.type === 'formula'
            || this.requestedParams.fields?.some(requested => requested.value === field.value));
    }
}
